import { useEffect, useState } from "react";
import { Flame, Check } from "lucide-react";
import { NovaOrb } from "../components/NovaOrb";
import { BottomNav } from "../components/BottomNav";
import { api } from "../lib/api";

const SLOT_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner" };
const EFFORT_LABEL = { low: "Low effort", med: "Some effort" };

const MealCard = ({ meal, isSelected, onSelect }) => (
  <button
    onClick={() => onSelect(meal.id)}
    data-testid={`meal-option-${meal.id}`}
    className={`w-full text-left rounded-2xl p-4 border transition-colors duration-300 ${
      isSelected ? "border-kaizen-violet bg-kaizen-violet/10" : "border-white/[0.08] bg-white/[0.02] hover:border-white/20"
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm text-kaizen-text font-medium">{meal.name}</p>
      {isSelected && <Check size={15} className="text-kaizen-violetSoft shrink-0 mt-0.5" data-testid={`meal-selected-check-${meal.id}`} />}
    </div>
    <p className="text-xs text-kaizen-dim mt-1.5 leading-relaxed">{meal.protein_note}</p>
    <span className="inline-block mt-2 text-[10px] uppercase tracking-wide text-kaizen-violetSoft/70 border border-kaizen-violetSoft/25 rounded-full px-2 py-0.5">
      {EFFORT_LABEL[meal.effort_level] || meal.effort_level}
    </span>
  </button>
);

const SlotSection = ({ slot, meals, selectedId, onSelect }) => (
  <div className="glass-card rounded-3xl p-5" data-testid={`meal-slot-${slot}`}>
    <p className="text-xs uppercase tracking-widest text-kaizen-dim mb-3">{SLOT_LABEL[slot]}</p>
    <div className="space-y-2.5">
      {meals.map((meal) => (
        <MealCard key={meal.id} meal={meal} isSelected={selectedId === meal.id} onSelect={(id) => onSelect(slot, id)} />
      ))}
    </div>
  </div>
);

export default function Meals() {
  const [data, setData] = useState(null);
  const [notReady, setNotReady] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/meals/today");
      setData(d);
      setNotReady(false);
    } catch {
      setNotReady(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const select = async (slot, mealId) => {
    setData((d) => ({ ...d, selected: { ...d.selected, [slot]: mealId } }));
    await api.post("/meals/select", { slot, meal_id: mealId });
  };

  return (
    <div className="min-h-screen bg-kaizen-bg text-kaizen-text pb-28" data-testid="meals-page">
      <div className="max-w-md mx-auto px-5 pt-8">
        <p className="font-display text-2xl">Today's meals</p>
        <p className="text-sm text-kaizen-dim mt-1">Three simple picks per meal — tap the one that fits.</p>

        <div className="flex justify-center mt-6">
          <NovaOrb size={70} />
        </div>

        {notReady ? (
          <div className="glass-card rounded-3xl p-6 mt-8 text-center" data-testid="meals-not-ready">
            <p className="text-sm text-kaizen-dim">Complete onboarding first so Nova can line up today's meals.</p>
          </div>
        ) : !data ? (
          <div className="flex justify-center mt-8">
            <NovaOrb size={50} />
          </div>
        ) : (
          <div className="space-y-4 mt-6">
            {["breakfast", "lunch", "dinner"].map((slot) => (
              <SlotSection
                key={slot}
                slot={slot}
                meals={data.options[slot] || []}
                selectedId={data.selected?.[slot]}
                onSelect={select}
              />
            ))}
            <div className="flex items-center gap-1.5 justify-center text-[11px] text-kaizen-dim2 mt-2">
              <Flame size={11} /> Every note is about protein, never calories.
            </div>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
